import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import SectionAccents from "@/components/SectionAccents";
import { api } from "@/lib/api";

const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "popular", label: "Popular" },
    { value: "price_low", label: "Price · Low to High" },
    { value: "price_high", label: "Price · High to Low" },
    { value: "featured", label: "Featured" },
];

const OCCASIONS = ["Birthday", "Anniversary", "Wedding", "Corporate", "Memorial", "Gift"];

const Shop = () => {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const q = params.get("q") || "";
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sort, setSort] = useState(params.get("sort") || "featured");
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [occasion, setOccasion] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/categories");
            setCategories(data.categories);
        })();
    }, []);

    useEffect(() => {
        setLoading(true);
        (async () => {
            const { data } = await api.get("/products", {
                params: {
                    category: category || undefined,
                    q: q || undefined,
                    sort,
                    min_price: priceRange[0] || undefined,
                    max_price: priceRange[1] < 10000 ? priceRange[1] : undefined,
                    occasion: occasion || undefined,
                    limit: 60,
                },
            });
            setProducts(data.products);
            setLoading(false);
        })();
    }, [category, q, sort, priceRange, occasion]);

    const setParam = (key, val) => {
        const next = new URLSearchParams(params);
        if (val) next.set(key, val); else next.delete(key);
        setParams(next);
    };

    const currentCategory = useMemo(() => categories.find((c) => c.slug === category), [categories, category]);

    return (
        <div className="relative max-w-7xl mx-auto px-6 py-10 overflow-hidden" data-testid="shop-page">
            <SectionAccents variant="shop" />
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">The Collection</div>
            <div className="flex items-end justify-between flex-wrap gap-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mt-1">
                    {currentCategory?.name || (q ? `Results for "${q}"` : "Shop All Miniatures")}
                </h1>
                <div className="flex items-center gap-2">
                    <button data-testid="filters-toggle" onClick={() => setShowFilters((s) => !s)} className="md:hidden inline-flex items-center gap-2 rounded-full glass px-4 h-10 text-sm font-medium">
                        <SlidersHorizontal className="w-4 h-4" /> Filters
                    </button>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort" className="rounded-full glass px-4 h-10 text-sm font-medium border-none focus:ring-2 focus:ring-primary/20 outline-none">
                        {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </div>
            {currentCategory?.description && <p className="text-muted-foreground mt-2 max-w-2xl">{currentCategory.description}</p>}

            <div className="mt-8 grid md:grid-cols-[240px_1fr] gap-8">
                {/* Sidebar */}
                <aside className={`space-y-6 ${showFilters ? "block" : "hidden md:block"}`} data-testid="shop-sidebar">
                    <div className="rounded-3xl glass p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-display font-semibold text-sm uppercase tracking-widest">Categories</h4>
                            {(category || occasion || q) && (
                                <button onClick={() => { setParams({}); setOccasion(""); }} className="text-[11px] text-primary flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
                            )}
                        </div>
                        <div className="space-y-1">
                            <button
                                onClick={() => setParam("category", "")}
                                className={`w-full text-left px-3 py-2 rounded-xl text-sm ${!category ? "bg-primary/10 text-primary font-semibold" : "hover:bg-primary/5"}`}
                            >All</button>
                            {categories.map((c) => (
                                <button
                                    key={c.slug}
                                    onClick={() => setParam("category", c.slug)}
                                    data-testid={`filter-category-${c.slug}`}
                                    className={`w-full text-left px-3 py-2 rounded-xl text-sm ${category === c.slug ? "bg-primary/10 text-primary font-semibold" : "hover:bg-primary/5"}`}
                                >{c.name}</button>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl glass p-4">
                        <h4 className="font-display font-semibold text-sm uppercase tracking-widest mb-3">Price (₹)</h4>
                        <input type="range" min="0" max="10000" step="500" value={priceRange[1]} onChange={(e) => setPriceRange([0, Number(e.target.value)])} className="w-full accent-primary" />
                        <div className="text-xs text-muted-foreground mt-1">Under ₹{priceRange[1].toLocaleString("en-IN")}</div>
                    </div>

                    <div className="rounded-3xl glass p-4">
                        <h4 className="font-display font-semibold text-sm uppercase tracking-widest mb-3">Occasion</h4>
                        <div className="flex flex-wrap gap-2">
                            {OCCASIONS.map((o) => (
                                <button
                                    key={o}
                                    onClick={() => setOccasion(occasion === o ? "" : o)}
                                    className={`text-xs px-3 py-1.5 rounded-full border ${occasion === o ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
                                >{o}</button>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Grid */}
                <div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-3xl bg-muted/30 animate-pulse" />)}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="rounded-3xl glass p-16 text-center">
                            <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center"><i className="fa-solid fa-magnifying-glass text-primary" /></div>
                            <p className="font-display text-xl">No products found</p>
                            <p className="text-sm text-muted-foreground mt-1">Try clearing filters or a different search.</p>
                        </div>
                    ) : (
                        <motion.div className="grid grid-cols-2 md:grid-cols-3 gap-4" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.04 } } }}>
                            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Shop;
